/**
 * Small copy-to-clipboard button for debug panels.
 * Shows a brief "Copied!" flash on success.
 */

import { Check, Copy } from "lucide-react";
import { useCallback } from "react";
import { Button } from "./Button.tsx";
import { useTransientFeedback } from "./shared.tsx";
import {
  COLOR_BORDER_ACCENT,
  COLOR_TEXT_DIM,
  FONT_SIZE_TINY,
  RADIUS_XS,
} from "./tokens.ts";

const buttonStyle: React.CSSProperties = {
  background: "none",
  border: `1px solid ${COLOR_BORDER_ACCENT}`,
  borderRadius: RADIUS_XS,
  color: COLOR_TEXT_DIM,
  cursor: "pointer",
  padding: "1px 4px",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: FONT_SIZE_TINY - 1,
  fontFamily: "monospace",
  lineHeight: 1,
  verticalAlign: "middle",
};

export function CopyButton({
  getText,
  label = "Copy",
  copiedLabel = "Copied",
}: {
  getText: () => string;
  label?: string;
  copiedLabel?: string;
}) {
  const [copied, fire] = useTransientFeedback();

  const handleClick = useCallback(() => {
    const text = getText();
    navigator.clipboard.writeText(text).then(fire);
  }, [getText, fire]);

  return (
    <Button
      data-no-drag
      onClick={handleClick}
      variant="ghost"
      size="xs"
      leftIcon={copied ? <Check size={9} /> : <Copy size={9} />}
      style={{
        ...buttonStyle,
        color: copied ? "#0f0" : COLOR_TEXT_DIM,
        borderColor: copied ? "rgba(0,255,0,0.3)" : COLOR_BORDER_ACCENT,
      }}
      title={label}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}
